import React from "react";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { MessageBanner, MessageType } from "../../../../repertoire/components/messageBanner/MessageBanner";

export interface IActionPromptResult {
    body?: React.ReactNode;
    message?: string;
}

export interface IActionPromptAction {
    text: string;
    buttonClassName?: string;
    onClick: () => IActionPromptResult | void | Promise<IActionPromptResult | void>;
}

export interface IActionPromptViewModalProps {
    body: React.ReactNode;
    cancelText: string;
    onCancel: () => void;
    actions: IActionPromptAction[];
    title?: string;
}

const ActionPromptView: React.FC<IActionPromptViewModalProps> = ({
    body,
    cancelText,
    onCancel,
    actions,
    title,
}) => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [message, setMessage] = React.useState<string>();
    const [displayBody, setDisplayBody] = React.useState<React.ReactNode>(body);
    const isMounted = React.useRef(true);
    const submittingRef = React.useRef(false);

    React.useEffect(() => () => {
        isMounted.current = false;
    }, []);

    const runAction = async (action: IActionPromptAction) => {
        if (submittingRef.current) {
            return;
        }

        submittingRef.current = true;
        setIsSubmitting(true);
        setMessage(undefined);

        try {
            const result = await action.onClick();
            if (isMounted.current && result) {
                setMessage(result.message);
                if (result.body !== undefined) {
                    setDisplayBody(result.body);
                }
            }
        } finally {
            submittingRef.current = false;
            if (isMounted.current) {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <div className="modal actionPromptView">
            <div className="modal-header">
                <button type="button" className="close" aria-hidden="true" disabled={isSubmitting} onClick={onCancel}>
                    <Dismiss20Regular title={cancelText} />
                </button>
                <h1 className="modal-title">{title}</h1>
            </div>
            <div className="fixedPart" />
            <div className="dynamicPart">
                <div className="modalBody modal-message">{displayBody}</div>
                <div className="modalBody">
                    {message && (
                        <div className="actionPromptMessage">
                            <MessageBanner
                                messages={[message]}
                                messageType={MessageType.Danger}
                                resetMessageBanner={() => setMessage(undefined)}
                            />
                        </div>
                    )}
                    <div className="actionPromptActions">
                        <button
                            type="button"
                            className="button btn-defaultSecondary btn-sm"
                            disabled={isSubmitting}
                            onClick={onCancel}
                        >
                            {cancelText}
                        </button>
                        <div className="actionPromptSubmitActions">
                            {actions.map((action, index) => (
                                <button
                                    key={`${action.text}-${index}`}
                                    type="button"
                                    className={`button ${action.buttonClassName ?? "btn-defaultSecondary"} btn-sm`}
                                    disabled={isSubmitting}
                                    onClick={() => runAction(action)}
                                >
                                    {action.text}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className="fixedPart" />
        </div>
    );
};

export default ActionPromptView;
